import React, { useState, useEffect } from 'react';
import { Mail, Plus, Eye, Edit, Trash2, Send, Users, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../config/axios';
import { useContentFilters } from '../../hooks/useContentFilters';
import ContentFilterBar from './ContentFilterBar';
import EmailAssetCreation from '../assets/by-type/EmailAssetCreation';

interface Email {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'pending_review' | 'approved' | 'published' | 'rejected';
  metadata?: string; // JSON string containing email_type, subject_line, recipients_count, etc.
  scheduled_for?: string;
  created_by?: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  updated_at?: string;
  // Parsed from metadata
  email_type?: string;
  subject_line?: string;
  recipients_count?: number;
  preview_text?: string;
  priority?: string;
  client_name?: string;
  project_id?: number;
  project_name?: string;
  phase_id?: number;
  phase_name?: string;
}

export default function EmailManagement() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Use content filters hook
  const {
    filters,
    grouping,
    filteredItems,
    groupedItems,
    activeFilterCount,
    updateFilter,
    clearFilters,
    removeFilter,
    setGrouping,
    isGrouped,
  } = useContentFilters(emails, 'email');

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/content?type=email');
      if (response.data.items) {
        // Parse metadata for each email
        const parsedEmails = response.data.items.map((email: any) => {
          let metadata = {};
          try {
            metadata = email.metadata ? JSON.parse(email.metadata) : {};
          } catch (e) {
            console.warn('Failed to parse metadata for email:', email.id);
          }
          return {
            ...email,
            email_type: metadata.email_type || 'newsletter',
            subject_line: metadata.subject_line || email.title,
            recipients_count: metadata.recipients_count || 0,
            preview_text: metadata.preview_text || email.description,
            ...metadata
          };
        });
        setEmails(parsedEmails);
      }
    } catch (error) {
      console.error('Failed to load emails:', error);
      setEmails([]); // Empty array on error - no mock data
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (email: Email) => {
    navigate(`/dashboard/content/${email.id}/preview`);
  };

  const handleEdit = (email: Email) => {
    navigate(`/dashboard/content/${email.id}/edit`);
  };

  const handleDelete = async (email: Email) => {
    if (!confirm(`Are you sure you want to delete "${email.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/content/${email.id}`);
      loadEmails();
    } catch (error) {
      console.error('Failed to delete email:', error);
      alert('Failed to delete email');
    }
  };

  const getEmailTypeIcon = (type?: string) => {
    if (!type) return Mail;

    switch (type.toLowerCase()) {
      case 'newsletter': return Mail;
      case 'promotional': return Send;
      case 'transactional': return Users;
      case 'drip': return Calendar;
      case 'survey': return Users;
      default: return Mail;
    }
  };

  const getEmailTypeBadge = (type?: string) => {
    if (!type) return 'bg-gray-100 text-gray-700';

    const colors: Record<string, string> = {
      newsletter: 'bg-blue-100 text-blue-700',
      promotional: 'bg-purple-100 text-purple-700',
      transactional: 'bg-green-100 text-green-700',
      drip: 'bg-orange-100 text-orange-700',
      survey: 'bg-cyan-100 text-cyan-700',
    };
    return colors[type.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
      pending_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-700', label: 'Approved' },
      published: { color: 'bg-blue-100 text-blue-700', label: 'Published' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  // Mock data for filters
  const mockClients = [
    { id: 1, name: 'Acme Corporation' },
    { id: 2, name: 'TechStart Inc' },
  ];

  const mockProjects = [
    { id: 1, name: 'Q1 Email Campaign' },
    { id: 2, name: 'Product Launch Series' },
  ];

  const mockPhases = [
    { id: 1, name: 'Awareness' },
    { id: 2, name: 'Nurture' },
    { id: 3, name: 'Conversion' },
  ];

  const renderEmailCard = (email: Email) => {
    const TypeIcon = getEmailTypeIcon(email.email_type);
    return (
      <div key={email.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="p-2 bg-blue-50 rounded-lg">
              <TypeIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 truncate">{email.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{email.subject_line}</p>
            </div>
          </div>
          {getStatusBadge(email.status)}
        </div>

        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Client:</span>
            <span className="font-medium text-gray-900">{email.client_name || `Client #${email.client_id || 'N/A'}`}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Type:</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getEmailTypeBadge(email.email_type)}`}>
              {email.email_type || 'Newsletter'}
            </span>
          </div>
          {email.recipients_count && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Recipients:</span>
              <span className="font-medium text-gray-900">{email.recipients_count.toLocaleString()}</span>
            </div>
          )}
          {email.scheduled_for && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Scheduled:</span>
              <span className="font-medium text-gray-900">{new Date(email.scheduled_for).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {email.preview_text && (
          <p className="text-xs text-gray-500 mb-3 line-clamp-2">{email.preview_text}</p>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePreview(email)}
            className="flex-1 flex items-center justify-center px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition text-xs font-medium"
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            Preview
          </button>
          <button
            onClick={() => handleEdit(email)}
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(email)}
            className="p-1.5 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded transition"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage email marketing campaigns
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Email
        </button>
      </div>

      {/* Filters */}
      <ContentFilterBar
        contentType="email"
        filters={filters}
        grouping={grouping}
        activeFilterCount={activeFilterCount}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        onRemoveFilter={removeFilter}
        onGroupingChange={(changes) => setGrouping({ ...grouping, ...changes })}
        clients={mockClients}
        projects={mockProjects}
        phases={mockPhases}
      />

      {/* List View */}
      {!isGrouped && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((email) => {
                  const TypeIcon = getEmailTypeIcon(email.email_type);
                  return (
                    <tr key={email.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                            <TypeIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{email.title}</div>
                            <div className="text-xs text-gray-500">{email.subject_line}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{email.client_name || `Client #${email.client_id || 'N/A'}`}</div>
                        <div className="text-xs text-gray-500">{email.project_name || 'No project'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEmailTypeBadge(email.email_type)}`}>
                          {email.email_type || 'Newsletter'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(email.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {email.scheduled_for ? new Date(email.scheduled_for).toLocaleDateString() : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Eye className="h-4 w-4" /></button>
                          <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Edit className="h-4 w-4" /></button>
                          <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grouped View */}
      {isGrouped && (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([groupName, groupEmails]) => {
            // Handle nested grouping structure
            const emailsArray = Array.isArray(groupEmails) ? groupEmails : [];
            const isExpanded = expandedGroups.has(groupName);

            return (
            <div key={groupName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
              >
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {groupName} ({emailsArray.length})
                </h3>
              </button>
              {isExpanded && (
              <div>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {emailsArray.map((email: Email) => {
                        const TypeIcon = getEmailTypeIcon(email.email_type);
                        return (
                          <tr key={email.id} className="hover:bg-gray-50 transition">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg"><TypeIcon className="h-5 w-5 text-blue-600" /></div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{email.title}</div>
                                  <div className="text-xs text-gray-500">{email.subject_line}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{email.client_name || `Client #${email.client_id || 'N/A'}`}</div>
                              <div className="text-xs text-gray-500">{email.project_name || 'No project'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEmailTypeBadge(email.email_type)}`}>
                                {email.email_type || 'Newsletter'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(email.status)}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{email.scheduled_for ? new Date(email.scheduled_for).toLocaleDateString() : '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Eye className="h-4 w-4" /></button>
                                <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Edit className="h-4 w-4" /></button>
                                <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No emails found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeFilterCount > 0
                ? 'Try adjusting your filters'
                : 'Get started by creating your first email campaign'}
            </p>
            {activeFilterCount === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Email
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create Email Modal - Full Screen */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Create Email Campaign</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  loadEmails(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render Email Asset Creation Component */}
            <div className="p-6">
              <EmailAssetCreation />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
